import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

import StudentHeader from "@/components/student/StudentHeader";
import StudentMobileNav from "@/components/student/StudentMobileNav";
import DashboardTab from "@/components/dashboard/DashboardTab";
import DecisionsTab from "@/components/decisions/DecisionsTab";
import CompanyTab from "@/components/dashboard/CompanyTab";
import MarketTab from "@/components/dashboard/MarketTab";
import MarketShareSection from "@/components/dashboard/MarketShareSection";
import ComparativeAnalytics from "@/components/dashboard/ComparativeAnalytics";
import RouteMapSection from "@/components/dashboard/RouteMapSection";
import PerformanceHistory from "@/components/dashboard/PerformanceHistory";
import TeamCollaboration from "@/components/dashboard/TeamCollaboration";
import AIAssist from "@/components/dashboard/AIAssist";
import Achievements from "@/components/dashboard/Achievements";

export default function StudentDashboard() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [gameRes, teamRes] = await Promise.all([
        axios.get(`${API}/games/${gameId}`),
        axios.get(`${API}/my-team/${gameId}`)
      ]);
      setGame(gameRes.data);
      setTeam(teamRes.data);

      if (teamRes.data && gameRes.data.current_quarter > 0) {
        const [kpisRes, decisionRes] = await Promise.all([
          axios.get(`${API}/teams/${teamRes.data.id}/kpis`),
          axios.get(`${API}/teams/${teamRes.data.id}/decisions/${gameRes.data.current_quarter}`)
        ]);
        setKpis(kpisRes.data);
        setDecision(decisionRes.data);
      }
    } catch (error) {
      toast.error("Failed to load game data");
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-sky-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading game...</p>
        </div>
      </div>
    );
  }

  const TAB_CONTENT = {
    dashboard: <DashboardTab game={game} team={team} kpis={kpis} onRefresh={fetchData} />,
    decisions: <DecisionsTab game={game} team={team} decision={decision} setDecision={setDecision} onRefresh={fetchData} />,
    company: <CompanyTab game={game} team={team} />,
    market: <MarketTab game={game} team={team} />,
    marketshare: <MarketShareSection game={game} team={team} />,
    compare: <ComparativeAnalytics game={game} team={team} />,
    routemap: <RouteMapSection game={game} team={team} />,
    history: <PerformanceHistory game={game} team={team} />,
    team: <TeamCollaboration game={game} team={team} />,
    ai: <AIAssist game={game} team={team} />,
    achievements: <Achievements team={team} />,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <StudentHeader
        game={game}
        team={team}
        gameId={gameId}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <StudentMobileNav activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" key={activeTab}>
        {TAB_CONTENT[activeTab]}
      </main>
    </div>
  );
}
